import React, { useEffect, useState, useRef, useMemo } from "react";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { useShallow } from "zustand/react/shallow";
import { useEffectiveProjectConfiguration } from "@presentation/components/helpers/useEffectiveProjectConfiguration";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { useInjection } from "@presentation/components/dependencyInjection";
import { ListSoftwareVersionsService } from "@application/useCases/softwareVersions/ListSoftwareVersionsService";
import { licensedSoftwareOrNull, shouldQualifyWithEngineName, useLicensedSoftware } from "./useLicensedSoftware";

const normalizeVersion = (value: string) => value.trim().replace(/^(?:SN|SM)\s*/i, "");
const normalizeVersionForLabel = (value: string, prefix: string) => {
  const normalized = normalizeVersion(value);
  return normalized ? `${prefix} ${normalized}` : "";
};

export const VersionSelectSettingsForm: React.FC = () => {
  // Store & Service
  const { softwareVersion, software, setSoftwareVersion } = useJobSetupStore(
    useShallow((state) => ({
      softwareVersion: state.CreateJobState.softwareVersion,
      software: state.CreateJobState.software ?? "Spectronaut",
      setSoftwareVersion: state.setSoftwareVersion,
    })),
  );
  const injectedVersionsService = useInjection(ListSoftwareVersionsService);
  const versionsService = useRef(injectedVersionsService).current;
  const licensedSoftware = licensedSoftwareOrNull(useLicensedSoftware());
  const { activeProjectId, projects } = useWorkspaceContextStore(
    useShallow((state) => ({
      activeProjectId: state.activeProjectId,
      projects: state.projects,
    })),
  );
  const softwareVersionReference = useRef(softwareVersion);
  const [candidateVersions, setCandidateVersions] = useState<string[]>([]);
  /**
   * Which engine the currently held candidate list was resolved for, or null
   * before the first resolution.
   *
   * Needed because "no versions are available" and "versions have not loaded
   * yet" are both an empty array, and the reconciliation effect below treats an
   * absent version as grounds for clearing the user's selection. Without this a
   * pre-set version - a duplicated job, or a restored draft - is wiped on first
   * render and replaced a moment later, which the user sees as the form losing
   * their choice.
   *
   * It records the engine rather than a boolean because switching engines
   * restarts the request while the previous engine's list is still held. A
   * boolean would stay true across that switch, and reconciliation would then
   * judge the new engine's selection against the old engine's candidates.
   */
  const [resolvedForSoftware, setResolvedForSoftware] = useState<string | null>(null);
  // SpectroMine has no team-configured allow-list yet, so scoping below is Spectronaut-only.
  const isSpectronaut = software !== "SpectroMine";
  const selectedSoftware = isSpectronaut ? "Spectronaut" : "SpectroMine";
  // Only reconcile against candidates that belong to the engine now selected.
  const isCandidatesResolved = resolvedForSoftware === selectedSoftware;
  const currentProjectId = useMemo(() => (projects.some((project) => project.id === activeProjectId) ? activeProjectId : ""), [activeProjectId, projects]);
  const effectiveConfiguration = useEffectiveProjectConfiguration(currentProjectId);
  useEffect(() => {
    let isCancelled = false;

    const loadCandidateVersions = async () => {
      try {
        const versions = await versionsService.getCandidateVersionsListAsync(selectedSoftware);
        if (isCancelled) {
          return;
        }

        const normalized = [...new Set((versions ?? []).map((version) => normalizeVersion(version)).filter(Boolean))].toSorted((a, b) => b.localeCompare(a, undefined, { numeric: true }));

        setCandidateVersions(normalized);
        setResolvedForSoftware(selectedSoftware);
      } catch {
        if (!isCancelled) {
          setCandidateVersions([]);
          setResolvedForSoftware(selectedSoftware);
        }
      }
    };

    loadCandidateVersions();

    return () => {
      isCancelled = true;
    };
  }, [versionsService, selectedSoftware]);

  const allowedVersions = useMemo(() => {
    // Configured versions only apply once a project context has actually resolved.
    // Without one there is no team/project configuration to inherit, so the
    // service candidates govern. SpectroMine has no team/project-configured allow-list yet.
    const configuredAllowedVersions = isSpectronaut && currentProjectId ? (effectiveConfiguration.allowedSnVersions ?? []) : [];
    const source = configuredAllowedVersions.length > 0 ? configuredAllowedVersions : candidateVersions;
    const normalized = source.map((version) => normalizeVersion(version)).filter(Boolean);
    return [...new Set(normalized)].toSorted((a, b) => b.localeCompare(a, undefined, { numeric: true }));
  }, [candidateVersions, currentProjectId, effectiveConfiguration.allowedSnVersions, isSpectronaut]);
  const configuredDefaultVersion = useMemo(() => {
    if (!isSpectronaut) {
      return allowedVersions[0] ?? null;
    }
    const configured = normalizeVersion(effectiveConfiguration.defaultSnVersion ?? "");
    return allowedVersions.includes(configured) ? configured : (allowedVersions[0] ?? null);
  }, [allowedVersions, effectiveConfiguration.defaultSnVersion, isSpectronaut]);

  const versionLegend = shouldQualifyWithEngineName(licensedSoftware) ? (software === "SpectroMine" ? "SpectroMine Version" : "Spectronaut Version") : "Version";

  useEffect(() => {
    softwareVersionReference.current = softwareVersion;
  }, [softwareVersion]);

  // UI State
  const [versionsList, setVersionsList] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSize, setDropdownSize] = useState({ width: 450, height: 400 });
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const dropdownReference = useRef<HTMLDivElement>(null);
  const triggerReference = useRef<HTMLDivElement>(null);
  const searchInputReference = useRef<HTMLInputElement>(null);

  // 1. Use the resolved team/project configured versions for the selected engine.
  useEffect(() => {
    setVersionsList(allowedVersions);

    // Nothing to reconcile against until the candidate list has resolved.
    // Acting on the initial empty array would clear a legitimate selection.
    if (!isCandidatesResolved) {
      return;
    }

    const currentSoftwareVersion = normalizeVersion(softwareVersionReference.current ?? "");
    if (currentSoftwareVersion) {
      if (!allowedVersions.includes(currentSoftwareVersion)) {
        setSoftwareVersion(configuredDefaultVersion ?? "");
        return;
      }

      if ((softwareVersionReference.current ?? "") !== currentSoftwareVersion) {
        setSoftwareVersion(currentSoftwareVersion);
      }
      return;
    }

    if (configuredDefaultVersion) {
      setSoftwareVersion(configuredDefaultVersion);
    }
  }, [allowedVersions, isCandidatesResolved, setSoftwareVersion, configuredDefaultVersion]);

  // 2. Grouping Logic
  const groupedData = useMemo(() => {
    const filtered = versionsList.filter((v) => v.toLowerCase().includes(searchTerm.toLowerCase()));
    const groups: Record<string, string[]> = {};

    for (const v of filtered) {
      const major = v.split(".", 1)[0];
      if (!Object.hasOwn(groups, major)) groups[major] = [];
      groups[major].push(v);
    }

    // Sort versions within each group and sort groups by major version (descending)
    const sortedGroups: Record<string, string[]> = {};
    const sortedMajors = Object.keys(groups).toSorted((a, b) => b.localeCompare(a, undefined, { numeric: true }));
    for (const major of sortedMajors) {
      sortedGroups[major] = groups[major].toSorted((a, b) => b.localeCompare(a, undefined, { numeric: true }));
    }

    return sortedGroups;
  }, [versionsList, searchTerm]);

  // 3. Drag & Resize Handlers
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setDropdownPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
      }
      if (!isResizing || !dropdownReference.current) return;
      const rect = dropdownReference.current.getBoundingClientRect();
      setDropdownSize({
        width: Math.max(300, e.clientX - rect.left),
        height: Math.max(200, e.clientY - rect.top),
      });
    };
    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };
    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart]);

  useEffect(() => {
    if (!(dropdownOpen && triggerReference.current)) {
      return;
    }

    const rect = triggerReference.current.getBoundingClientRect();
    setDropdownPosition({ x: rect.left, y: rect.bottom + 5 });
    setTimeout(() => searchInputReference.current?.focus(), 0);
  }, [dropdownOpen]);

  // Versions belong to a search engine, so with no licensed engine there is
  // nothing to choose from and the block is hidden entirely.
  if (licensedSoftware !== null && licensedSoftware.length === 0) {
    return null;
  }

  let noVersionsMessage = "No SpectroMine image tags are available from the registry.";
  if (isSpectronaut) {
    noVersionsMessage =
      allowedVersions.length > 0
        ? "No allowed Spectronaut versions are configured for this team or project."
        : "No Spectronaut versions configured. Please configure allowed versions in the team settings.";
  }

  return (
    <fieldset className="k-form-fieldset" style={{ marginTop: "10px" }}>
      <legend className="k-form-legend">
        <strong>{versionLegend}</strong>
      </legend>

      {/* Selector Box */}
      <div
        ref={triggerReference}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        style={{
          border: "1px solid #ccc",
          borderRadius: "3px",
          padding: "8px 12px",
          backgroundColor: "white",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{softwareVersion ? normalizeVersionForLabel(softwareVersion, isSpectronaut ? "SN" : "SM") : "Select version..."}</span>
        <span style={{ fontSize: "10px", color: "#666" }}>{dropdownOpen ? "▲" : "▼"}</span>
      </div>

      {dropdownOpen && (
        <>
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setDropdownOpen(false)} />
          <div
            ref={dropdownReference}
            style={{
              position: "fixed",
              left: dropdownPosition.x,
              top: dropdownPosition.y,
              width: dropdownSize.width,
              height: dropdownSize.height,
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "3px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <div
              style={{ padding: "8px 12px", backgroundColor: "#f5f5f5", cursor: "move", display: "flex", justifyContent: "space-between" }}
              onMouseDown={(e) => {
                setIsDragging(true);
                const rect = dropdownReference.current?.getBoundingClientRect();
                if (rect) setDragStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
              }}
            >
              <span style={{ fontWeight: "600", fontSize: "13px" }}>Versions</span>
              <button onClick={() => setDropdownOpen(false)} style={{ border: "none", background: "none", cursor: "pointer" }}>
                ×
              </button>
            </div>

            {/* Search */}
            <div style={{ padding: "10px 10px 0" }}>
              <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "6px" }}>
                New versions found in the registry are available automatically. You can manage which versions are available on the Software Versions page.
              </div>
            </div>
            <div style={{ padding: "0 10px 10px" }}>
              <input
                ref={searchInputReference}
                style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "3px" }}
                placeholder="Search version..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* List with Grouping */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {versionsList.length === 0 && <div style={{ padding: "12px", color: "#666", fontSize: "13px" }}>{noVersionsMessage}</div>}
              {Object.entries(groupedData).map(([major, value]) => (
                <div key={major}>
                  <div style={{ padding: "4px 12px", backgroundColor: "#fafafa", fontSize: "11px", fontWeight: "bold", color: "#888", borderBottom: "1px solid #eee" }}>VERSION {major}</div>
                  {value.map((v) => (
                    <div
                      key={v}
                      style={{
                        padding: "8px 24px",
                        cursor: "pointer",
                        backgroundColor: normalizeVersion(softwareVersion ?? "") === v ? "#e3f2fd" : "transparent",
                      }}
                      onClick={() => {
                        setSoftwareVersion(v);
                        setDropdownOpen(false);
                      }}
                    >
                      <span style={{ fontSize: "13px" }}>{normalizeVersionForLabel(v, isSpectronaut ? "SN" : "SM")}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Resize Handle */}
            <div
              style={{ position: "absolute", bottom: 0, right: 0, width: "15px", height: "15px", cursor: "se-resize", background: "linear-gradient(135deg, transparent 50%, #999 50%)" }}
              onMouseDown={(e) => {
                e.stopPropagation();
                setIsResizing(true);
              }}
            />
          </div>
        </>
      )}
    </fieldset>
  );
};
